import { useSyncExternalStore } from 'react';
import {
  BusinessActionEnum,
  BusinessProductAreaV2,
  intersectPayformGrants,
  type BusinessDataV2Type,
  BusinessAccessPolicyModeV2,
} from '@am3nd-org/pointcraft-types';

import { CONFIG } from 'src/environments/environment';
import { useDeviceSyncStore } from 'src/store/DeviceSync';
import { getSessionGeneration, registerSessionCleanup } from 'src/auth/session-lifecycle';

import { usePointPaySession } from '../auth/session-context';
import { resolveLegacyRoleId, type PayformsPrincipal } from './usePayformsAccess';

function pointPayGrants(
  user: PayformsPrincipal | null,
  business: Pick<BusinessDataV2Type, 'access_v2' | 'isSuperAdmin'> | null
) {
  if (!user?.isActive || !business) return null;
  if (user.isSuperAdmin || business.isSuperAdmin)
    return { privileged: true, productAreas: [], actions: [] };
  const access = business.access_v2;
  if (!access) return null;
  const role =
    access.roleDefinitions.find((candidate) => candidate.id === user.roleId) ??
    access.roleDefinitions.find((candidate) => candidate.id === resolveLegacyRoleId(user));
  const grants =
    access.businessAccess.policy.mode === BusinessAccessPolicyModeV2.BUSINESS_ONLY
      ? access.businessAccess
      : intersectPayformGrants(
          access.businessAccess,
          role?.grants ?? { productAreas: [], actions: [] }
        );
  return { ...grants, privileged: false };
}
export function pointPayReadCapabilities(
  user: PayformsPrincipal | null,
  business: Pick<BusinessDataV2Type, 'access_v2' | 'isSuperAdmin'> | null
) {
  const grants = pointPayGrants(user, business);
  return {
    customers:
      !!grants &&
      (grants.privileged ||
        (grants.productAreas.includes(BusinessProductAreaV2.CUSTOMERS) &&
          grants.actions.includes(BusinessActionEnum.CUSTOMER_PROFILE_USE))),
    payments:
      !!grants &&
      (grants.privileged || grants.productAreas.includes(BusinessProductAreaV2.TRANSACTIONS_V4)),
  };
}
/** General payment requests do not require a Payforms product grant. */
export function pointPayLinkCapabilities(
  user: PayformsPrincipal | null,
  business: Pick<BusinessDataV2Type, 'access_v2' | 'isSuperAdmin'> | null
) {
  const grants = pointPayGrants(user, business);
  const paymentLinks =
    !!grants &&
    (grants.privileged ||
      grants.productAreas.includes(BusinessProductAreaV2.PAYMENTS) ||
      grants.productAreas.includes(BusinessProductAreaV2.TRANSACTIONS_V4));
  return {
    paymentLinks,
    managePaymentLinks:
      paymentLinks &&
      !!grants &&
      (grants.privileged || grants.actions.includes(BusinessActionEnum.PAYMENT_CREATE)),
  };
}

/** Financial action: ceiling and explicit role grants are both required in every policy mode. */
export function pointPayCustomerPaymentCapability(
  user: PayformsPrincipal | null,
  business: Pick<BusinessDataV2Type, 'access_v2' | 'isSuperAdmin'> | null
) {
  if (!user?.isActive || !business) return false;
  if (user.isSuperAdmin || business.isSuperAdmin) return true;
  const access = business.access_v2;
  const role =
    access?.roleDefinitions.find((candidate) => candidate.id === user.roleId) ??
    access?.roleDefinitions.find((candidate) => candidate.id === resolveLegacyRoleId(user));
  if (!access || !role) return false;
  const grants = intersectPayformGrants(access.businessAccess, role.grants);
  return (
    grants.productAreas.includes(BusinessProductAreaV2.CUSTOMERS) &&
    grants.productAreas.includes(BusinessProductAreaV2.PAYMENTS) &&
    grants.actions.includes(BusinessActionEnum.CUSTOMER_PROFILE_USE) &&
    grants.actions.includes(BusinessActionEnum.PAYMENT_CREATE)
  );
}

export function pointPayManualCardCapability(
  user: PayformsPrincipal | null,
  business: Pick<BusinessDataV2Type, 'access_v2' | 'isSuperAdmin'> | null
) {
  if (!user?.isActive || !business) return false;
  if (user.isSuperAdmin || business.isSuperAdmin) return true;
  const access = business.access_v2;
  const role =
    access?.roleDefinitions.find((candidate) => candidate.id === user.roleId) ??
    access?.roleDefinitions.find((candidate) => candidate.id === resolveLegacyRoleId(user));
  if (!access || !role) return false;
  const grants = intersectPayformGrants(access.businessAccess, role.grants);
  return (
    grants.actions.includes(BusinessActionEnum.PAYMENT_CREATE) &&
    (grants.productAreas.includes(BusinessProductAreaV2.PAYMENTS) ||
      grants.productAreas.includes(BusinessProductAreaV2.TRANSACTIONS_V4))
  );
}

/** Visibility/cache scope only; the API independently authorizes every read. */
export function usePointPayReadContext() {
  const identity = usePointPaySession();
  const business = useDeviceSyncStore((state) => state.businessData);
  const users = useDeviceSyncStore((state) => state.userData);
  const providers = useDeviceSyncStore((state) => state.providerDetails);
  const accountRevision = useDeviceSyncStore((state) => state.versions?.providerDetails);
  const generation = useSyncExternalStore(
    registerSessionCleanup,
    getSessionGeneration,
    getSessionGeneration
  );
  const user = identity ? users.find((candidate) => candidate.id === identity.userId) : null;
  const principal = user
    ? { roleId: user.roleId, isActive: user.isActive, isDefault: user.isDefault }
    : null;
  const businessId = identity?.businessId === business?.id ? (business?.id ?? null) : null;
  const access = pointPayReadCapabilities(principal, businessId ? business : null);
  const links = pointPayLinkCapabilities(principal, businessId ? business : null);
  return {
    ...access,
    ...links,
    createManualCardPayments: pointPayManualCardCapability(principal, businessId ? business : null),
    createCustomerPayments: pointPayCustomerPaymentCapability(
      principal,
      businessId ? business : null
    ),
    paymentRecoveryKey: JSON.stringify([CONFIG.environment, businessId, identity?.uid]),
    businessId,
    scopeKey:
      businessId && identity && (access.customers || access.payments || links.paymentLinks)
        ? JSON.stringify([
            businessId,
            identity.uid,
            generation,
            CONFIG.environment,
            principal,
            business?.isSuperAdmin,
            business?.access_v2,
            providers,
            accountRevision,
          ])
        : null,
  };
}
